import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  PortalGatewayError,
  type ConversationDeletion,
  type ConversationEntry,
  type ConversationResume,
  type ConversationSummary,
  type PortalGateway,
} from "./api";

type LoadState = "unavailable" | "loading" | "ready" | "error";
type ResumeState = "idle" | "preparing" | "ready" | "error";

export type ConversationHistoryState = {
  conversations: ConversationSummary[];
  conversationsState: LoadState;
  conversationsErrorCode: string | null;
  conversationsNextCursor: string | null;
  selectedConversation: ConversationSummary | null;
  entries: ConversationEntry[];
  entriesState: LoadState;
  entriesErrorCode: string | null;
  entriesNextCursor: string | null;
  deletingEntryId: string | null;
  deletionOutcomes: Readonly<Record<string, ConversationDeletion["disposition"]>>;
  deletionErrorCodes: Readonly<Record<string, string>>;
  resume: ConversationResume | null;
  resumeState: ResumeState;
  resumeErrorCode: string | null;
};

const initialState = (
  gateway: PortalGateway | null,
): ConversationHistoryState => ({
  conversations: [],
  conversationsState: gateway ? "loading" : "unavailable",
  conversationsErrorCode: null,
  conversationsNextCursor: null,
  selectedConversation: null,
  entries: [],
  entriesState: gateway ? "ready" : "unavailable",
  entriesErrorCode: null,
  entriesNextCursor: null,
  deletingEntryId: null,
  deletionOutcomes: {},
  deletionErrorCodes: {},
  resume: null,
  resumeState: "idle",
  resumeErrorCode: null,
});

const failureCode = (error: unknown): string =>
  error instanceof PortalGatewayError
    ? error.code
    : "PORTAL_REQUEST_FAILED";

const wasAborted = (error: unknown): boolean =>
  error instanceof DOMException && error.name === "AbortError";

export type ConversationHistoryController = ConversationHistoryState & {
  selectConversation: (conversation: ConversationSummary) => Promise<void>;
  closeConversation: () => void;
  loadMoreConversations: () => Promise<void>;
  loadMoreEntries: () => Promise<void>;
  refreshConversations: () => Promise<void>;
  requestDeletion: (entryId: string) => Promise<void>;
  prepareResume: () => Promise<void>;
  clearResume: (errorCode?: string) => void;
};

export const useConversationHistory = (
  gateway: PortalGateway | null,
): ConversationHistoryController => {
  const [state, setState] = useState<ConversationHistoryState>(() =>
    initialState(gateway),
  );
  const conversationsController = useRef<AbortController | null>(null);
  const entriesController = useRef<AbortController | null>(null);
  const mutationController = useRef<AbortController | null>(null);

  const readConversations = useCallback(
    async (cursor: string | null, append: boolean): Promise<void> => {
      if (!gateway) {
        setState(initialState(null));
        return;
      }
      conversationsController.current?.abort();
      const controller = new AbortController();
      conversationsController.current = controller;
      setState((current) => ({
        ...current,
        conversationsState:
          append && current.conversations.length > 0
            ? current.conversationsState
            : "loading",
        conversationsErrorCode: null,
      }));
      try {
        const page = await gateway.listConversations(
          cursor,
          controller.signal,
        );
        setState((current) => {
          const merged = append
            ? [...current.conversations, ...page.items]
            : page.items;
          const identities = new Set<string>();
          const unique = merged.filter((item) => {
            if (identities.has(item.conversation_id)) {
              return false;
            }
            identities.add(item.conversation_id);
            return true;
          });
          return {
            ...current,
            conversations: unique,
            conversationsState: "ready",
            conversationsErrorCode: null,
            conversationsNextCursor: page.next_cursor,
            selectedConversation:
              unique.find(
                (item) =>
                  item.conversation_id ===
                  current.selectedConversation?.conversation_id,
              ) ?? current.selectedConversation,
          };
        });
      } catch (error) {
        if (!wasAborted(error)) {
          setState((current) => ({
            ...current,
            conversationsState: "error",
            conversationsErrorCode: failureCode(error),
          }));
        }
      } finally {
        if (conversationsController.current === controller) {
          conversationsController.current = null;
        }
      }
    },
    [gateway],
  );

  useEffect(() => {
    setState(initialState(gateway));
    void readConversations(null, false);
    return () => {
      conversationsController.current?.abort();
      entriesController.current?.abort();
      mutationController.current?.abort();
    };
  }, [gateway, readConversations]);

  const readEntries = useCallback(
    async (
      conversation: ConversationSummary,
      cursor: string | null,
      append: boolean,
    ): Promise<void> => {
      if (!gateway) {
        return;
      }
      entriesController.current?.abort();
      const controller = new AbortController();
      entriesController.current = controller;
      setState((current) => ({
        ...current,
        entriesState:
          append && current.entries.length > 0
            ? current.entriesState
            : "loading",
        entriesErrorCode: null,
      }));
      try {
        const page = await gateway.listConversationEntries(
          conversation.conversation_id,
          cursor,
          controller.signal,
        );
        setState((current) => {
          if (
            current.selectedConversation?.conversation_id !==
            conversation.conversation_id
          ) {
            return current;
          }
          return {
            ...current,
            entries: append
              ? [...current.entries, ...page.items]
              : page.items,
            entriesState: "ready",
            entriesErrorCode: null,
            entriesNextCursor: page.next_cursor,
          };
        });
      } catch (error) {
        if (!wasAborted(error)) {
          setState((current) => ({
            ...current,
            entriesState: "error",
            entriesErrorCode: failureCode(error),
          }));
        }
      } finally {
        if (entriesController.current === controller) {
          entriesController.current = null;
        }
      }
    },
    [gateway],
  );

  const selectConversation = useCallback(
    async (conversation: ConversationSummary): Promise<void> => {
      mutationController.current?.abort();
      setState((current) => ({
        ...current,
        selectedConversation: conversation,
        entries: [],
        entriesState: gateway ? "loading" : "unavailable",
        entriesErrorCode: null,
        entriesNextCursor: null,
        deletingEntryId: null,
        deletionOutcomes: {},
        deletionErrorCodes: {},
        resume: null,
        resumeState: "idle",
        resumeErrorCode: null,
      }));
      await readEntries(conversation, null, false);
    },
    [gateway, readEntries],
  );

  const closeConversation = useCallback(() => {
    entriesController.current?.abort();
    mutationController.current?.abort();
    setState((current) => ({
      ...current,
      selectedConversation: null,
      entries: [],
      entriesState: gateway ? "ready" : "unavailable",
      entriesErrorCode: null,
      entriesNextCursor: null,
      deletingEntryId: null,
      deletionOutcomes: {},
      deletionErrorCodes: {},
      resume: null,
      resumeState: "idle",
      resumeErrorCode: null,
    }));
  }, [gateway]);

  const loadMoreConversations = useCallback(async (): Promise<void> => {
    if (
      state.conversationsNextCursor === null ||
      state.conversationsState === "loading"
    ) {
      return;
    }
    await readConversations(state.conversationsNextCursor, true);
  }, [
    readConversations,
    state.conversationsNextCursor,
    state.conversationsState,
  ]);

  const loadMoreEntries = useCallback(async (): Promise<void> => {
    if (
      state.selectedConversation === null ||
      state.entriesNextCursor === null ||
      state.entriesState === "loading"
    ) {
      return;
    }
    await readEntries(
      state.selectedConversation,
      state.entriesNextCursor,
      true,
    );
  }, [
    readEntries,
    state.entriesNextCursor,
    state.entriesState,
    state.selectedConversation,
  ]);

  const refreshConversations = useCallback(
    async (): Promise<void> => readConversations(null, false),
    [readConversations],
  );

  const requestDeletion = useCallback(
    async (entryId: string): Promise<void> => {
      if (!gateway || state.deletingEntryId !== null) {
        return;
      }
      mutationController.current?.abort();
      const controller = new AbortController();
      mutationController.current = controller;
      setState((current) => ({
        ...current,
        deletingEntryId: entryId,
        deletionErrorCodes: Object.fromEntries(
          Object.entries(current.deletionErrorCodes).filter(
            ([identifier]) => identifier !== entryId,
          ),
        ),
      }));
      try {
        const result = await gateway.deleteConversationEntry(
          entryId,
          controller.signal,
        );
        setState((current) => ({
          ...current,
          deletingEntryId: null,
          deletionOutcomes: {
            ...current.deletionOutcomes,
            [entryId]: result.disposition,
          },
          resume: null,
          resumeState: current.resume ? "error" : current.resumeState,
          resumeErrorCode: current.resume
            ? "HISTORY_CHANGED_REPREPARE"
            : current.resumeErrorCode,
        }));
        await readConversations(null, false);
      } catch (error) {
        if (!wasAborted(error)) {
          setState((current) => ({
            ...current,
            deletingEntryId: null,
            deletionErrorCodes: {
              ...current.deletionErrorCodes,
              [entryId]: failureCode(error),
            },
          }));
        }
      } finally {
        if (mutationController.current === controller) {
          mutationController.current = null;
        }
      }
    },
    [gateway, readConversations, state.deletingEntryId],
  );

  const prepareResume = useCallback(async (): Promise<void> => {
    if (
      !gateway ||
      state.selectedConversation === null ||
      state.resumeState === "preparing"
    ) {
      return;
    }
    mutationController.current?.abort();
    const controller = new AbortController();
    mutationController.current = controller;
    setState((current) => ({
      ...current,
      resume: null,
      resumeState: "preparing",
      resumeErrorCode: null,
    }));
    try {
      const result = await gateway.prepareConversationResume(
        state.selectedConversation.conversation_id,
        controller.signal,
      );
      setState((current) => ({
        ...current,
        resume: result,
        resumeState: "ready",
        resumeErrorCode: null,
      }));
    } catch (error) {
      if (!wasAborted(error)) {
        setState((current) => ({
          ...current,
          resume: null,
          resumeState: "error",
          resumeErrorCode: failureCode(error),
        }));
      }
    } finally {
      if (mutationController.current === controller) {
        mutationController.current = null;
      }
    }
  }, [gateway, state.resumeState, state.selectedConversation]);

  const clearResume = useCallback((errorCode?: string) => {
    setState((current) => ({
      ...current,
      resume: null,
      resumeState: errorCode ? "error" : "idle",
      resumeErrorCode: errorCode ?? null,
    }));
  }, []);

  return {
    ...state,
    selectConversation,
    closeConversation,
    loadMoreConversations,
    loadMoreEntries,
    refreshConversations,
    requestDeletion,
    prepareResume,
    clearResume,
  };
};
